import React, { FunctionComponent } from 'react';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import Box from 'app/common/MuiBox';
import {
  AppBar,
  Toolbar,
  Avatar,
  Divider,
  ListSubheader,
  Popover,
  ButtonBase,
  MenuList,
  MenuItem,
  Typography,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExitToApp from '@mui/icons-material/ExitToApp';
import LockOutlined from '@mui/icons-material/LockOutlined';
import { TagType, Control } from 'app/services/api';
import PATHS from 'app/utils/paths';
import logo from 'assets/images/cse-logo-h-3x.png';
import {
  UserDataType,
  oauthChangePassword,
  oauthLogout,
} from 'app/services/auth';
import {
  platformColorRoles,
  platformMotion,
  platformRadius,
  platformRegisterPalette,
  platformShadow,
} from 'app/common/platformVisualGrammar';

interface NavBarProps {
  currentUser: UserDataType;
  selectedTag: TagType;
  selectedControl: Control;
}

type NavMenuItem = {
  label: string;
  to: string;
  matchPaths?: string[];
};

type WorkspaceNavGroup = {
  label: string;
  items: NavMenuItem[];
};

const navLinksLayoutStyle: React.CSSProperties = {
  columnGap: 16,
};

const navButtonLinkLayoutStyle: React.CSSProperties = {
  display: 'inline-flex',
  flexShrink: 0,
  textDecoration: 'none',
};

const navMenuItemLayoutStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-start',
  padding: '8.8px 10px 8.8px 22px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  width: '100%',
};

const rootSx: SxProps<Theme> = {
  flexGrow: 1,
};

const appBarSx: SxProps<Theme> = {
  boxShadow: 'none',
  borderBottom: `1px solid ${platformColorRoles.border}`,
};

const toolbarSx: SxProps<Theme> = {
  minHeight: 80,
  overflow: 'hidden',
};

const toolbarContentSx: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 2,
  minWidth: 0,
};

const leftSectionSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
};

const logoSx: SxProps<Theme> = (theme) => ({
  height: 65,
  display: 'inline-block',
  marginRight: theme.spacing(8),
  flexShrink: 0,
});

const navLinksSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  columnGap: 2,
  minWidth: 0,
  overflowX: 'auto',
  overflowY: 'hidden',
  whiteSpace: 'nowrap',
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': {
    height: 6,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: platformColorRoles.border,
    borderRadius: `${platformRadius.pill}px`,
  },
};

const navMenuButtonSx =
  (active: boolean): SxProps<Theme> =>
  (theme) => ({
    height: 80,
    padding: theme.spacing(0, 1.5),
    marginRight: 0,
    borderRadius: 0,
    borderBottom: '3px solid transparent',
    color: active ? theme.palette.primary.main : platformColorRoles.text,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 600,
    textDecoration: 'none',
    transition: `color ${platformMotion.stateFast}, border-color ${platformMotion.stateFast}`,
    ...(active ? { borderBottomColor: theme.palette.primary.main } : {}),
    '&:hover': {
      color: theme.palette.primary.main,
    },
    '&:focus-visible': {
      outline: `2px solid ${platformColorRoles.focus}`,
      outlineOffset: -4,
    },
  });

const navMenuButtonContentSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
};

const navMenuPopoverPaperSx: SxProps<Theme> = {
  mt: 1,
  borderRadius: `${platformRadius.mini}px`,
  border: `1px solid ${platformColorRoles.borderSoft}`,
  boxShadow: platformShadow.overlay,
  minWidth: 248,
  p: 0.75,
};

const navMenuSubheaderSx: SxProps<Theme> = {
  backgroundColor: 'transparent',
  color: platformColorRoles.textMuted,
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  lineHeight: 1.2,
  px: 1.25,
  py: 0.75,
  textTransform: 'uppercase',
};

const navMenuSubheaderSpacedSx: SxProps<Theme> = {
  mt: 0.75,
};

const navMenuItemSx =
  (active: boolean): SxProps<Theme> =>
  (theme) => ({
    borderRadius: `${platformRadius.mini}px`,
    color: active ? theme.palette.primary.main : platformColorRoles.text,
    display: 'flex',
    fontWeight: 600,
    justifyContent: 'flex-start',
    padding: theme.spacing(1.1, 1.25, 1.1, 2.75),
    textDecoration: 'none',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    width: '100%',
    ...(active ? { backgroundColor: platformRegisterPalette.accentSoft } : {}),
    '&:hover': {
      backgroundColor: platformRegisterPalette.hoverBackground,
    },
    '&:focus-visible': {
      outline: `2px solid ${platformColorRoles.focus}`,
      outlineOffset: 2,
    },
  });

const rightMenuSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};

const accountTriggerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  borderRadius: `${platformRadius.mini}px`,
  py: 0.75,
  px: 1.25,
  gap: 1,
  border: '1px solid transparent',
  transition: `background-color ${platformMotion.stateFast}, border-color ${platformMotion.stateFast}`,
  '&:hover': {
    backgroundColor: platformRegisterPalette.accentSoft,
    borderColor: platformColorRoles.borderSoft,
  },
  '&:focus-visible': {
    outline: `2px solid ${platformColorRoles.focus}`,
    outlineOffset: 2,
  },
};

const accountAvatarSx: SxProps<Theme> = (theme) => ({
  width: 32,
  height: 32,
  fontSize: theme.typography.pxToRem(14),
  flexShrink: 0,
});

const accountNameSx: SxProps<Theme> = {
  maxWidth: 180,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: platformColorRoles.text,
  fontWeight: 600,
};

const accountChevronSx: SxProps<Theme> = {
  color: platformColorRoles.textMuted,
  flexShrink: 0,
};

const accountPopoverPaperSx: SxProps<Theme> = {
  mt: 1,
  borderRadius: `${platformRadius.surface}px`,
  border: `1px solid ${platformColorRoles.borderSoft}`,
  boxShadow: platformShadow.overlay,
  minWidth: 304,
  overflow: 'hidden',
};

const accountCardSx: SxProps<Theme> = {
  backgroundColor: '#FFFFFF',
};

const accountHeaderSx: SxProps<Theme> = {
  display: 'flex',
  gap: 1.5,
  alignItems: 'center',
  p: 2,
};

const accountHeaderAvatarSx: SxProps<Theme> = (theme) => ({
  width: 44,
  height: 44,
  fontSize: theme.typography.pxToRem(18),
  flexShrink: 0,
});

const accountMetaSx: SxProps<Theme> = {
  minWidth: 0,
};

const accountDisplayNameSx: SxProps<Theme> = {
  color: platformColorRoles.text,
  fontWeight: 700,
  lineHeight: 1.25,
};

const accountCompanySx: SxProps<Theme> = {
  color: platformColorRoles.text,
  mt: 0.25,
  lineHeight: 1.3,
};

const accountEmailSx: SxProps<Theme> = {
  color: platformColorRoles.textMuted,
  mt: 0.35,
  lineHeight: 1.3,
  wordBreak: 'break-word',
};

const accountSectionSx: SxProps<Theme> = {
  p: 0.75,
};

const navMenuListSx: SxProps<Theme> = {
  p: 0,
};

const accountActionSx: SxProps<Theme> = {
  width: '100%',
  borderRadius: `${platformRadius.mini}px`,
  justifyContent: 'flex-start',
  py: 1.1,
  px: 1.25,
  gap: 1.25,
  color: platformColorRoles.text,
  fontWeight: 600,
  textAlign: 'left',
  '&:hover': {
    backgroundColor: platformRegisterPalette.hoverBackground,
  },
  '&:focus-visible': {
    outline: `2px solid ${platformColorRoles.focus}`,
    outlineOffset: 2,
  },
};

const accountActionDangerSx: SxProps<Theme> = {
  color: '#B42318',
  '&:hover': {
    backgroundColor: '#FEF3F2',
  },
};

const accountActionIconSx: SxProps<Theme> = {
  color: 'inherit',
  fontSize: 20,
  flexShrink: 0,
};

const NavBar: FunctionComponent<NavBarProps> = ({
  currentUser,
  selectedTag,
  selectedControl,
}: NavBarProps) => {
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [knowledgeAnchor, setKnowledgeAnchor] =
    React.useState<HTMLElement | null>(null);
  const [navMenuAnchor, setNavMenuAnchor] = React.useState<HTMLElement | null>(
    null
  );

  const getDestination = (baseUrl: string) => {
    let destination = baseUrl.slice();

    if (selectedTag) {
      destination += `${PATHS.GROUPS}/${selectedTag.id}`;
    }

    if (
      selectedControl &&
      (selectedTag ||
        baseUrl === PATHS.DASHBOARD ||
        baseUrl === PATHS.KPI ||
        baseUrl === PATHS.ANALYZE)
    ) {
      destination += `${PATHS.CONTROLS}/${selectedControl.id}`;
    }

    return destination;
  };

  const getControlsDestination = () =>
    selectedControl
      ? `${PATHS.DASHBOARD}${PATHS.CONTROLS}/${selectedControl.id}`
      : `${PATHS.DASHBOARD}${PATHS.CONTROLS}`;

  if (!currentUser) {
    return null;
  }

  const openPopover = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const closePopover = () => setAnchorEl(null);
  const closeNavMenu = () => {
    setNavMenuAnchor(null);
  };

  const onClickChangePassword = () => {
    closePopover();
    oauthChangePassword();
  };

  const onClickLogout = () => {
    closePopover();
    oauthLogout();
  };

  const openNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setNavMenuAnchor(event.currentTarget);
  };

  const isActivePath = (paths: string[]) =>
    paths.some((path) => location.pathname.startsWith(path));
  const pathMatchesMenuItem = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);
  const getMenuItemMatchPaths = (item: NavMenuItem) =>
    item.matchPaths || [item.to];

  const adminAllowed =
    currentUser.permissions.includes('read:config') ||
    currentUser.permissions.includes('read:global');
  const peopleAllowed = currentUser.permissions.includes('read:users');

  const workspaceNavGroups: WorkspaceNavGroup[] = [
    {
      label: 'Protection Model',
      items: [
        {
          label: 'Lenses',
          to: selectedTag
            ? `${PATHS.DASHBOARD}${PATHS.GROUPS}/${selectedTag.id}`
            : `${PATHS.DASHBOARD}${PATHS.GROUPS}`,
        },
        {
          label: 'Controls',
          to: getControlsDestination(),
          matchPaths: [
            `${PATHS.DASHBOARD}${PATHS.CONTROLS}`,
            `${PATHS.KPI}${PATHS.CONTROLS}`,
            `${PATHS.ANALYZE}${PATHS.CONTROLS}`,
          ],
        },
        { label: 'Frameworks', to: PATHS.FRAMEWORKS },
      ],
    },
    {
      label: 'Operating Workspace',
      items: [
        { label: 'Resources', to: PATHS.RESOURCES },
        { label: 'Assets', to: PATHS.ASSETS },
        { label: 'Projects', to: PATHS.PROJECTS },
        ...(peopleAllowed ? [{ label: 'People', to: PATHS.PEOPLE }] : []),
      ],
    },
    {
      label: 'Oversight',
      items: [
        {
          label: 'Connections',
          to: PATHS.CONNECTIONS,
          matchPaths: [PATHS.CONNECTIONS, PATHS.ACTIVITY],
        },
        { label: 'Risk Register', to: PATHS.RISK_REGISTER },
      ],
    },
  ];

  const activeMenuItems = workspaceNavGroups.flatMap((group) => group.items);
  const activeMenuItemLabel = activeMenuItems
    .flatMap((item) =>
      getMenuItemMatchPaths(item).map((path) => ({
        item,
        path,
      }))
    )
    .filter(({ path }) => pathMatchesMenuItem(path))
    .sort((left, right) => right.path.length - left.path.length)[0]?.item.label;

  return (
    <Box sx={rootSx}>
      <AppBar position="static" color="inherit" elevation={0} sx={appBarSx}>
        <Toolbar sx={toolbarSx}>
          <Box sx={toolbarContentSx}>
            <Box sx={leftSectionSx}>
              <Link to="/">
                <Box component="img" src={logo} sx={logoSx} alt="cse logo" />
              </Link>
              <Box sx={navLinksSx} style={navLinksLayoutStyle}>
                <ButtonBase
                  sx={navMenuButtonSx(
                    isActivePath([
                      PATHS.DASHBOARD,
                      PATHS.KPI,
                      PATHS.ANALYZE,
                      PATHS.PROJECTS,
                      PATHS.PEOPLE,
                      PATHS.RESOURCES,
                      PATHS.ASSETS,
                      PATHS.RISK_REGISTER,
                      PATHS.CONNECTIONS,
                      PATHS.ACTIVITY,
                      PATHS.FRAMEWORKS,
                    ])
                  )}
                  onClick={openNavMenu}
                >
                  <Box component="span" sx={navMenuButtonContentSx}>
                    Workspace
                    <ExpandMore fontSize="small" />
                  </Box>
                </ButtonBase>
                <Link
                  to={getDestination(PATHS.SEARCH)}
                  style={navButtonLinkLayoutStyle}
                >
                  <ButtonBase
                    sx={navMenuButtonSx(isActivePath([PATHS.SEARCH]))}
                  >
                    Search
                  </ButtonBase>
                </Link>
                {adminAllowed ? (
                  <Link to={PATHS.ADMIN} style={navButtonLinkLayoutStyle}>
                    <ButtonBase
                      sx={navMenuButtonSx(isActivePath([PATHS.ADMIN]))}
                    >
                      Settings
                    </ButtonBase>
                  </Link>
                ) : null}
                <ButtonBase
                  id="knowledge-base-menu-button"
                  sx={navMenuButtonSx(isActivePath([PATHS.KNOWLEDGE_BASE]))}
                  aria-haspopup="menu"
                  aria-expanded={Boolean(knowledgeAnchor)}
                  aria-controls={
                    knowledgeAnchor ? 'knowledge-base-menu' : undefined
                  }
                  onClick={(event) => setKnowledgeAnchor(event.currentTarget)}
                >
                  <Box component="span" sx={navMenuButtonContentSx}>
                    Knowledge Base
                    <ExpandMore fontSize="small" />
                  </Box>
                </ButtonBase>
              </Box>
            </Box>
            <Box sx={rightMenuSx}>
              <Divider orientation="vertical" flexItem />
              <Box ml={1}>
                <ButtonBase
                  sx={accountTriggerSx}
                  onClick={openPopover}
                  aria-label="Open account menu"
                >
                  <Avatar
                    sx={accountAvatarSx}
                    src={currentUser.userInfo?.picture}
                    alt={currentUser.name}
                  />
                  <Typography variant="body2" sx={accountNameSx}>
                    {currentUser.name}
                  </Typography>
                  <ExpandMore sx={accountChevronSx} />
                </ButtonBase>
              </Box>
            </Box>

            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={closePopover}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              slotProps={{ paper: { sx: accountPopoverPaperSx } }}
            >
              <Box sx={accountCardSx}>
                <Box sx={accountHeaderSx}>
                  <Avatar
                    sx={accountHeaderAvatarSx}
                    src={currentUser.userInfo?.picture}
                    alt={currentUser.name}
                  />
                  <Box sx={accountMetaSx}>
                    <Typography variant="body2" sx={accountDisplayNameSx}>
                      {currentUser.name}
                    </Typography>
                    <Typography variant="body2" sx={accountCompanySx}>
                      {currentUser.company.name}
                    </Typography>
                    {currentUser.userInfo?.email ? (
                      <Typography variant="caption" sx={accountEmailSx}>
                        {currentUser.userInfo.email}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>

                <Divider />

                <MenuList sx={accountSectionSx}>
                  <MenuItem
                    sx={accountActionSx}
                    onClick={onClickChangePassword}
                  >
                    <LockOutlined sx={accountActionIconSx} />
                    Change Password
                  </MenuItem>
                </MenuList>

                <Divider />

                <MenuList sx={accountSectionSx}>
                  <MenuItem
                    sx={[accountActionSx, accountActionDangerSx]}
                    onClick={onClickLogout}
                  >
                    <ExitToApp sx={accountActionIconSx} />
                    Sign Out
                  </MenuItem>
                </MenuList>
              </Box>
            </Popover>
            <Popover
              open={Boolean(knowledgeAnchor)}
              anchorEl={knowledgeAnchor}
              onClose={() => setKnowledgeAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{ paper: { sx: navMenuPopoverPaperSx } }}
            >
              <MenuList
                id="knowledge-base-menu"
                aria-labelledby="knowledge-base-menu-button"
                autoFocusItem={Boolean(knowledgeAnchor)}
              >
                {[
                  {
                    label: 'Intro',
                    description: 'Connect protection, investment and delivery.',
                    to: PATHS.INTRO,
                  },
                  {
                    label: 'Measurement that Matters',
                    description:
                      'Define capability. Measure coverage. Build confidence.',
                    to: PATHS.MEASUREMENT_THAT_MATTERS,
                  },
                  {
                    label: 'Control Stats Fallacy',
                    description:
                      'See the protection behind the security statistics.',
                    to: PATHS.CONTROL_STATS_FALLACY,
                  },
                  {
                    label: 'X-Day',
                    description: 'Understand why the threat level matters.',
                    to: PATHS.X_DAY,
                  },
                  {
                    label: 'Maturity Scale',
                    description:
                      'Understand the platform’s shared 0–5 maturity labels.',
                    to: PATHS.MATURITY_SCALE,
                  },
                ].map((deck) => (
                  <MenuItem
                    key={deck.to}
                    component={Link}
                    to={deck.to}
                    selected={location.pathname === deck.to}
                    sx={navMenuItemSx(location.pathname === deck.to)}
                    onClick={() => setKnowledgeAnchor(null)}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 310,
                        maxWidth: 'calc(100vw - 80px)',
                        whiteSpace: 'normal',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ display: 'block', fontWeight: 600 }}
                      >
                        {deck.label}
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          display: 'block',
                          mt: 0.25,
                          fontSize: 12,
                          lineHeight: 1.45,
                          color: 'text.secondary',
                        }}
                      >
                        {deck.description}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </MenuList>
            </Popover>
            <Popover
              open={Boolean(navMenuAnchor)}
              anchorEl={navMenuAnchor}
              onClose={closeNavMenu}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              slotProps={{ paper: { sx: navMenuPopoverPaperSx } }}
            >
              <MenuList sx={navMenuListSx}>
                {workspaceNavGroups.map((group, groupIndex) => (
                  <React.Fragment key={group.label}>
                    <ListSubheader
                      disableSticky
                      sx={[
                        navMenuSubheaderSx,
                        groupIndex > 0 ? navMenuSubheaderSpacedSx : null,
                      ]}
                    >
                      {group.label}
                    </ListSubheader>
                    {group.items.map((item) => {
                      const active = item.label === activeMenuItemLabel;
                      return (
                        <MenuItem
                          key={item.label}
                          component={Link}
                          to={item.to}
                          onClick={closeNavMenu}
                          style={navMenuItemLayoutStyle}
                          sx={navMenuItemSx(active)}
                        >
                          {item.label}
                        </MenuItem>
                      );
                    })}
                  </React.Fragment>
                ))}
              </MenuList>
            </Popover>
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
};

type mapStateType = {
  app: {
    tag: TagType;
    control: Control;
  };
};

const mapState = ({ app: { tag, control } }: mapStateType) => ({
  selectedTag: tag,
  selectedControl: control,
});

export default connect(mapState)(NavBar);
